"use client";

import { AnimatePresence, m as motion } from "framer-motion";
import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { useTranslations } from "next-intl";

import { Link, usePathname } from "@/i18n/navigation";

const GROUPS: { key: "development" | "design" | "growth"; href: string; items: { href: string; key: string }[] }[] = [
  {
    key: "development",
    href: "/services/development",
    items: [
      { href: "/services/development/mobile-web-apps", key: "services.development.mobileWebApps.title" },
      { href: "/services/development/ecommerce", key: "services.development.ecommerce.title" },
    ],
  },
  {
    key: "design",
    href: "/services/design",
    items: [
      { href: "/services/design/brand-identity", key: "services.design.brandIdentity.title" },
      { href: "/services/design/ui-ux", key: "services.design.uiUx.title" },
      { href: "/services/design/motion", key: "services.design.motion.title" },
    ],
  },
  {
    key: "growth",
    href: "/services/growth",
    items: [
      { href: "/services/growth/marketing-seo", key: "services.growth.marketingSeo.title" },
      { href: "/services/growth/strategy", key: "services.growth.strategy.title" },
    ],
  },
];

const TOP_LINKS: { href: string; key: string }[] = [
  { href: "/catalog", key: "nav.catalog" },
  { href: "/products", key: "nav.products" },
  { href: "/studio", key: "nav.studio" },
  { href: "/contact", key: "nav.contact" },
];

export function MobileMenu() {
  const t = useTranslations();
  const pathname = usePathname();
  const [open, setOpen] = useState(false);
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    setMounted(true);
  }, []);

  useEffect(() => {
    // Close the menu on every route change. This is a teardown-style
    // effect — it doesn't read open or compute from props, it just resets
    // state when navigation completes. Pulling this into the Link onClick
    // would miss programmatic navigations (router.push from anywhere).
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setOpen(false);
  }, [pathname]);

  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") setOpen(false);
    }
    window.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = prev;
      window.removeEventListener("keydown", onKey);
    };
  }, [open]);

  return (
    <>
      <button
        type="button"
        aria-label={open ? t("nav.menu_close") : t("nav.menu_open")}
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
        className="grid h-10 w-10 place-items-center rounded-full border-0 bg-transparent text-fg1"
      >
        <span className="relative block h-[14px] w-[20px]">
          <motion.span
            animate={{
              y: open ? 6 : 0,
              rotate: open ? 45 : 0,
            }}
            transition={{ duration: 0.25, ease: [0.22, 1, 0.36, 1] }}
            className="absolute left-0 top-0 block h-[2px] w-full rounded-full bg-fg1"
          />
          <motion.span
            animate={{ opacity: open ? 0 : 1 }}
            transition={{ duration: 0.15 }}
            className="absolute left-0 top-[6px] block h-[2px] w-full rounded-full bg-fg1"
          />
          <motion.span
            animate={{
              y: open ? -6 : 0,
              rotate: open ? -45 : 0,
            }}
            transition={{ duration: 0.25, ease: [0.22, 1, 0.36, 1] }}
            className="absolute left-0 bottom-0 block h-[2px] w-full rounded-full bg-fg1"
          />
        </span>
      </button>

      {mounted && createPortal(
        <AnimatePresence>
        {open ? (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="fixed inset-0 z-[90] flex flex-col overflow-y-auto px-5 pt-[80px] pb-16"
            style={{
              background: "rgba(10,10,15,0.97)",
              backdropFilter: "blur(18px) saturate(140%)",
              WebkitBackdropFilter: "blur(18px) saturate(140%)",
            }}
          >
            <motion.div
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1], delay: 0.05 }}
              className="flex flex-col gap-10"
            >
              <nav aria-label="Primary">
                <ul className="flex flex-col gap-4">
                  {TOP_LINKS.map((link) => (
                    <li key={link.href}>
                      <Link
                        href={link.href}
                        className="block font-display font-semibold text-fg1"
                        style={{
                          fontSize: "28px",
                          lineHeight: 1.1,
                          letterSpacing: "-0.02em",
                        }}
                      >
                        {t(link.key)}
                      </Link>
                    </li>
                  ))}
                </ul>
              </nav>

              <section
                className="border-t pt-8"
                style={{ borderColor: "var(--border)" }}
              >
                <span
                  className="mb-6 inline-flex font-mono text-[11px] uppercase text-fg3"
                  style={{ letterSpacing: "0.2em" }}
                >
                  {t("nav.dropdown_label")}
                </span>
                <ul className="flex flex-col gap-8">
                  {GROUPS.map((group) => (
                    <li key={group.key}>
                      <Link
                        href={group.href}
                        className="block font-display text-[20px] font-semibold text-fg1"
                        style={{ letterSpacing: "-0.015em" }}
                      >
                        {t(`nav.dropdown.${group.key}`)}
                      </Link>
                      <ul className="mt-3 flex flex-col gap-2">
                        {group.items.map((item) => (
                          <li key={item.href}>
                            <Link
                              href={item.href}
                              className="block text-[14px] text-fg3 transition-colors hover:text-fg1"
                            >
                              {t(item.key)}
                            </Link>
                          </li>
                        ))}
                      </ul>
                    </li>
                  ))}
                </ul>
              </section>
            </motion.div>
          </motion.div>
        ) : null}
        </AnimatePresence>,
        document.body
      )}
    </>
  );
}
